// composables/usePoolaTeaser.js
//
// Data layer for the lean Poola teaser page ("قناة عروض البولة"). Surfaces the
// LIVE collective Sellers-Club PoolShare total — the hero "pool" number — and
// keeps it fresh while the page is open. Nothing else: the countdown is the
// hero, the pool figure is the single supporting number.
//
// CONTRACT (read the real backend, don't guess — see
// ../backend/_bmad-output/sync/briefs/2026-06-21-poolshare-club-total-public-endpoint.md):
//   • GET /clubs/poolshare-total — Sanctum-auth, NOT membership-gated. Every
//     authenticated user (active member, supplier, or PENDING/under-review)
//     may read it. data = { totalActiveCents, memberCount } (integer cents).
//
// MONEY: the amount is integer cents from the backend. We keep it in integer
// cents and divide by 100 exactly once, for display only — never lossy float
// math on cents.
//
// NOTE: an earlier richer design also fetched the viewer's personal share and
// computed milestone/delta/social figures. The lean redesign dropped all of it;
// the per-user `/users/poolshare-summary` fetch and milestone/delta machinery
// are gone (no wasted network, no dead code). If a richer page returns, restore
// from git history rather than carrying unused machinery here.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { POOLA_POLL_MS } from '@/constants/poola.js';
import { clubPoolShareTotalService } from '@/services/clubPoolShareTotal.service.js';

/**
 * usePoolaTeaser — reactive data for PoolaPage.
 *
 * MUST be called from a component `setup()` — it registers onMounted/onUnmounted
 * lifecycle hooks to drive the poll loop and to clean up its timer + listeners.
 *
 * @returns {{
 *   totalEgp: import('vue').ComputedRef<number>,
 *   loading: import('vue').Ref<boolean>,
 *   error: import('vue').Ref<unknown>,
 *   hasLoaded: import('vue').Ref<boolean>,
 *   refresh: () => Promise<void>,
 * }}
 */
export function usePoolaTeaser() {
  // ── Club total (integer cents) ─────────────────────────────────────────────
  const totalActiveCents = ref(0);
  const loading = ref(false);
  const error = ref(null);
  // True once we have at least one successful fetch. Lets the page distinguish
  // "still loading the first value" (skeleton) from a genuine zero total, and
  // keeps showing the last good number if a later poll fails.
  const hasLoaded = ref(false);

  // Cents → EGP at the edge only (single divide-by-100, display use).
  const totalEgp = computed(() => totalActiveCents.value / 100);

  async function refresh() {
    loading.value = true;
    try {
      const res = await clubPoolShareTotalService.get();
      // jsonApiClient deserializes JSON:API → attributes are flattened onto data.
      const data = res?.data?.data ?? {};
      totalActiveCents.value = Number.isFinite(Number(data.totalActiveCents))
        ? Number(data.totalActiveCents)
        : 0;
      error.value = null;
      hasLoaded.value = true;
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  }

  // ── Poll loop — interval + resume/focus, only while the page is visible ─────
  let pollId = null;

  function startPolling() {
    if (pollId !== null) return;
    pollId = setInterval(() => {
      if (!document.hidden) refresh();
    }, POOLA_POLL_MS);
  }

  function stopPolling() {
    if (pollId !== null) {
      clearInterval(pollId);
      pollId = null;
    }
  }

  function onVisibilityChange() {
    if (document.hidden) {
      // Pause polling while hidden so we never fetch in the background.
      stopPolling();
    } else {
      // Catch up immediately on resume, then resume the interval.
      refresh();
      startPolling();
    }
  }

  function onFocus() {
    // App/window resume (TMA & PWA). Only refresh while actually visible.
    if (!document.hidden) refresh();
  }

  onMounted(() => {
    refresh();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    if (!document.hidden) startPolling();
  });

  onUnmounted(() => {
    stopPolling();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
  });

  return {
    totalEgp,
    loading,
    error,
    hasLoaded,
    refresh,
  };
}
